import React from 'react';
import { Link } from 'react-router-dom';
import {
  PRIVACY_EMAIL,
  LEGAL_ENTITY_NAME,
  BUSINESS_ADDRESS,
  PRIVACY_LAST_UPDATED,
} from '../../legal/privacyConstants';

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'information-we-collect', label: 'Information we collect' },
  { id: 'how-we-use', label: 'How we use information' },
  { id: 'legal-basis', label: 'Legal basis and consent' },
  { id: 'how-we-share', label: 'How we share information' },
  { id: 'cookies', label: 'Cookies and similar technologies' },
  { id: 'retention', label: 'Data retention' },
  { id: 'security', label: 'Security' },
  { id: 'your-rights', label: 'Your rights and choices' },
  { id: 'organization-admin', label: 'Organization and admin access' },
  { id: 'push-notifications', label: 'Mobile push notifications' },
  { id: 'children', label: "Children's privacy" },
  { id: 'changes', label: 'Changes to this policy' },
  { id: 'contact', label: 'Contact us' },
];

const sectionClass =
  'scroll-mt-24 mb-14 last:mb-0 border-b border-gray-200 dark:border-gray-700 pb-14 last:border-0 last:pb-0';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
            WorkLoop
          </span>
          <nav className="flex flex-wrap items-center gap-4 text-sm">
            <Link
              to="/terms"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Terms
            </Link>
            <Link
              to="/support"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Support
            </Link>
            <Link
              to="/app"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              About
            </Link>
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Create account
            </Link>
          </nav>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-14">
        <div className="lg:grid lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] lg:gap-12 xl:gap-16">
          <aside className="hidden lg:block">
            <nav
              className="sticky top-28 space-y-1 text-sm border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-white dark:bg-gray-800 shadow-sm"
              aria-label="On this page"
            >
              <p className="font-semibold text-gray-900 dark:text-white mb-3 px-2">
                On this page
              </p>
              <ul className="space-y-1">
                {tocItems.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="block px-2 py-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <main className="min-w-0 max-w-3xl">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
              Privacy Policy
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Last updated: {PRIVACY_LAST_UPDATED}
            </p>

            <div className="mt-8 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 sm:p-8 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                How WorkLoop handles your information
              </h2>
              <ul className="list-disc list-outside pl-5 space-y-2 text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                <li>
                  WorkLoop is a corporate communication platform for teams. We collect account
                  details, messages and files you share, and technical data needed to run the
                  service securely.
                </li>
                <li>
                  We use this information to provide messaging, channels, file storage, tasks,
                  announcements, and related collaboration features—and to keep the platform safe.
                </li>
                <li>
                  If you use WorkLoop through your employer, your organization’s administrators may
                  access certain workspace data under their policies.
                </li>
                <li>
                  We do not sell your personal information. You may have rights to access,
                  correct, or delete your data depending on where you live.
                </li>
                <li>
                  For questions, contact us using the details in the{' '}
                  <a href="#contact" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
                    Contact us
                  </a>{' '}
                  section below.
                </li>
              </ul>
            </div>

            <div className="mt-12 space-y-0 text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
              <section id="overview" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  1. Overview
                </h2>
                <p className="mb-4">
                  WorkLoop helps organizations communicate through channels, direct messages,
                  shared files, tasks, announcements, and integrations. This Privacy Policy
                  explains what personal data we collect, why we use it, who we share it with, how
                  long we keep it, and what choices you have.
                </p>
                <p>
                  We aim to keep this notice easy to find and written in plain language. This
                  document is a general description of our practices; your organization may impose
                  additional rules when you use WorkLoop as part of your job.
                </p>
              </section>

              <section id="information-we-collect" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  2. Information we collect
                </h2>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-6 mb-2">
                  Account and profile
                </h3>
                <p className="mb-4">
                  When you register or are provisioned by your organization, we may collect
                  identifiers and profile details such as username, email address, name, password
                  (stored using secure hashing), organization or workspace affiliation, role (for
                  example workspace admin or member), and optional profile information you or your
                  admin provide (such as job title or avatar).
                </p>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-6 mb-2">
                  Organization signup
                </h3>
                <p className="mb-4">
                  When an organization signs up for WorkLoop, we collect information needed to create
                  the workspace and an initial administrator account, including organization name,
                  organization identifier, and the owner’s account details.
                </p>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-6 mb-2">
                  Communications and collaboration content
                </h3>
                <p className="mb-4">
                  We process content you create or share in the service, including messages, channel
                  posts, reactions, mentions, saved messages, announcements, task titles and
                  descriptions, comments, reminders, and similar collaboration data. Metadata may
                  include timestamps, sender and recipient identifiers, channel membership, and
                  read or delivery indicators where the product provides them.
                </p>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-6 mb-2">
                  Files and media
                </h3>
                <p className="mb-4">
                  If you upload files or images, we store and process that content to display it to
                  authorized users in your workspace. Files are stored using secure cloud storage
                  and delivered through controlled access mechanisms (such as time-limited access
                  links) appropriate to the product configuration.
                </p>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-6 mb-2">
                  Technical and usage data
                </h3>
                <p className="mb-4">
                  We collect technical information needed to operate and secure the service,
                  including IP address, device or browser type, operating system, app version,
                  authentication events, diagnostic and error logs, and usage signals. Real-time
                  features may use WebSockets or similar connections; related connection metadata may
                  be logged for reliability and security.
                </p>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-6 mb-2">
                  Support and feedback
                </h3>
                <p>
                  If you contact support or submit feedback, we process the information you provide
                  in that interaction.
                </p>
              </section>

              <section id="how-we-use" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  3. How we use information
                </h2>
                <p className="mb-4">We use personal data to:</p>
                <ul className="list-disc list-outside pl-5 space-y-2">
                  <li>Provide, operate, and improve WorkLoop and its features;</li>
                  <li>Create and manage accounts, workspaces, and permissions;</li>
                  <li>Deliver messages, notifications, and real-time updates;</li>
                  <li>Store, sync, and display files and collaboration content you choose to share;</li>
                  <li>Maintain security, prevent abuse, and investigate suspicious activity;</li>
                  <li>Debug, monitor performance, and fix errors;</li>
                  <li>Provide customer support and respond to requests;</li>
                  <li>Send service-related notices (such as security or policy updates);</li>
                  <li>Comply with applicable law and enforce our terms.</li>
                </ul>
              </section>

              <section id="legal-basis" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  4. Legal basis and consent
                </h2>
                <p className="mb-4">
                  Depending on your location, different rules may apply to how we process personal
                  data. Where required, we rely on one or more of the following: performing our
                  contract with you or your organization; legitimate interests that are not
                  overridden by your rights (such as securing the service and improving reliability);
                  compliance with legal obligations; or your consent where we ask for it (for
                  example, for certain cookies or optional communications).
                </p>
                <p>
                  Where we rely on consent, we aim to make it informed and specific, and you may
                  withdraw consent where the law allows, subject to limitations needed to provide
                  the service or meet legal requirements.
                </p>
              </section>

              <section id="how-we-share" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  5. How we share information
                </h2>
                <p className="mb-4">We may share personal data with:</p>
                <ul className="list-disc list-outside pl-5 space-y-2 mb-4">
                  <li>
                    <strong className="text-gray-900 dark:text-white">Your organization</strong> —
                    workspace owners and administrators may access and export certain data according
                    to their policies and product permissions;
                  </li>
                  <li>
                    <strong className="text-gray-900 dark:text-white">Service providers</strong> —
                    vendors that help us host the application, store data, send email, authenticate
                    users, analyze reliability, or provide customer support, bound by contractual
                    obligations;
                  </li>
                  <li>
                    <strong className="text-gray-900 dark:text-white">Integrations</strong> — if you
                    or your admin enable third-party integrations (for example connectors to other
                    business systems), those providers may receive data you choose to send according
                    to their terms and your configuration;
                  </li>
                  <li>
                    <strong className="text-gray-900 dark:text-white">Legal and safety</strong> —
                    regulators, law enforcement, or others when required by law, or when we
                    reasonably believe disclosure is necessary to protect rights, safety, or the
                    integrity of the service.
                  </li>
                </ul>
                <p>We do not sell your personal information.</p>
              </section>

              <section id="cookies" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  6. Cookies and similar technologies
                </h2>
                <p className="mb-4">
                  WorkLoop may use cookies or similar technologies to keep you signed in, remember
                  preferences (such as display settings), maintain session state, measure basic
                  performance, and protect against fraud. Where non-essential cookies are used, we
                  will describe them in applicable cookie notices or settings and, where required,
                  obtain consent before use.
                </p>
                <p>
                  You can control cookies through your browser settings; disabling some cookies may
                  limit certain features.
                </p>
              </section>

              <section id="retention" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  7. Data retention
                </h2>
                <p className="mb-4">
                  We retain personal data for as long as needed to provide the service, comply with
                  legal obligations, resolve disputes, and enforce agreements. Retention periods may
                  also reflect your organization’s settings, archival practices, or legal hold
                  requirements.
                </p>
                <p>
                  When data is no longer needed, we delete or anonymize it in line with our
                  procedures, subject to backup and technical constraints.
                </p>
              </section>

              <section id="security" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  8. Security
                </h2>
                <p className="mb-4">
                  We use technical and organizational measures designed to protect personal data
                  against unauthorized access, loss, or misuse. These measures may include
                  encryption in transit, access controls, authentication, logging, and secure
                  infrastructure practices.
                </p>
                <p>
                  No method of transmission or storage is completely secure. If you believe your
                  account or data has been compromised, contact us promptly using the information
                  below.
                </p>
              </section>

              <section id="your-rights" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  9. Your rights and choices
                </h2>
                <p className="mb-4">
                  Depending on applicable law (which may include, for example, laws in the European
                  Economic Area, the United Kingdom, India, or other regions), you may have rights
                  to:
                </p>
                <ul className="list-disc list-outside pl-5 space-y-2 mb-4">
                  <li>Access the personal data we hold about you;</li>
                  <li>Request correction of inaccurate data;</li>
                  <li>Request deletion or restriction of certain processing;</li>
                  <li>Object to processing based on legitimate interests, where applicable;</li>
                  <li>Withdraw consent where processing is based on consent;</li>
                  <li>Request a copy of your data in a portable format, where applicable;</li>
                  <li>Lodge a complaint with a supervisory authority, where available.</li>
                </ul>
                <p className="mb-4">
                  If you use WorkLoop through your employer, some requests may need to be handled by
                  your organization’s administrator in addition to, or instead of, us. We will
                  assist as required by law.
                </p>
                <p>
                  To exercise rights or ask questions, use the contact details in the{' '}
                  <a href="#contact" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
                    Contact us
                  </a>{' '}
                  section.
                </p>
              </section>

              <section id="organization-admin" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  10. Organization and administrator access
                </h2>
                <p className="mb-4">
                  When you use WorkLoop as part of an organization, your workspace may be configured
                  and administered by that organization. Administrators may be able to view or
                  manage accounts, membership, certain communications, files, audit or export
                  capabilities, and security settings in line with the product’s features and the
                  organization’s internal policies.
                </p>
                <p>
                  If you have questions about how your employer processes your data, contact your
                  organization’s IT or legal team.
                </p>
              </section>

              <section id="push-notifications" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  11. Mobile push notifications
                </h2>
                <p className="mb-4">
                  If you use WorkLoop mobile applications, we may use push notification services
                  (such as platform-provided push infrastructure) to deliver alerts—for example,
                  for new messages or reminders. That process typically involves a device token or
                  similar identifier associated with your account.
                </p>
                <p>
                  You can turn off push notifications in your device or in-app settings where
                  available. Disabling notifications does not delete your account or message
                  history.
                </p>
              </section>

              <section id="children" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  12. Children’s privacy
                </h2>
                <p>
                  WorkLoop is intended for business and organizational use, not for children. We do
                  not knowingly collect personal information from anyone under the age required by
                  applicable law for consent to data processing. If you believe we have collected
                  such information, please contact us so we can take appropriate steps.
                </p>
              </section>

              <section id="changes" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  13. Changes to this policy
                </h2>
                <p className="mb-4">
                  We may update this Privacy Policy from time to time. When we do, we will revise
                  the “Last updated” date at the top of this page and, where appropriate, provide
                  additional notice (such as an in-app message or email).
                </p>
                <p>
                  We encourage you to review this policy periodically. Continued use of WorkLoop
                  after changes become effective constitutes your acknowledgment of the revised
                  policy, to the extent permitted by law.
                </p>
              </section>

              <section id="contact" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  14. Contact us
                </h2>
                <p className="mb-4">
                  If you have questions, concerns, or requests regarding this Privacy Policy or our
                  processing of personal data, contact us at:
                </p>
                <ul className="space-y-2 not-prose">
                  <li>
                    <span className="text-gray-500 dark:text-gray-400">Email: </span>
                    <a
                      href={`mailto:${PRIVACY_EMAIL}`}
                      className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                    >
                      {PRIVACY_EMAIL}
                    </a>
                  </li>
                  <li>
                    <span className="text-gray-500 dark:text-gray-400">Company: </span>
                    <span className="text-gray-900 dark:text-white">{LEGAL_ENTITY_NAME}</span>
                  </li>
                  <li>
                    <span className="text-gray-500 dark:text-gray-400">Address: </span>
                    <span className="text-gray-900 dark:text-white">{BUSINESS_ADDRESS}</span>
                  </li>
                </ul>
                <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">
                  This policy is provided for transparency and does not constitute legal advice.
                  Please have qualified counsel review it for your jurisdiction and deployment.
                </p>
              </section>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
